import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Search,
  MousePointerClick,
  AlertTriangle,
  Zap,
  Users,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react'
import type { Insights } from '../hooks/useInsights'

interface OverviewCardsProps {
  overview: Insights['overview'] | undefined
  isLoading: boolean
}

function Trend({ value, invert = false }: { value: number; invert?: boolean }) {
  if (value === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <Minus className="h-3 w-3" />
        no change
      </span>
    )
  }
  // For metrics where lower is better (zero-result rate), invert the colour
  const isPositive = invert ? value < 0 : value > 0
  const Icon = value > 0 ? TrendingUp : TrendingDown
  const colour = isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${colour}`}>
      <Icon className="h-3 w-3" />
      {value > 0 ? '+' : ''}
      {value}% vs prior
    </span>
  )
}

export function OverviewCards({ overview, isLoading }: OverviewCardsProps) {
  const cards = [
    {
      title: 'Searches',
      value: overview?.totalSearches ?? 0,
      icon: Search,
      format: (v: number) => v.toLocaleString(),
      trend: overview?.trend.searches ?? 0,
      invert: false,
    },
    {
      title: 'Zero-Result Rate',
      value: overview?.zeroResultRate ?? 0,
      icon: AlertTriangle,
      format: (v: number) => `${v.toFixed(1)}%`,
      trend: overview?.trend.zeroResultRate ?? 0,
      invert: true,
      sub: overview ? `${overview.zeroResultCount.toLocaleString()} queries` : '',
    },
    {
      title: 'Click-Through Rate',
      value: overview?.clickThroughRate ?? 0,
      icon: MousePointerClick,
      format: (v: number) => `${v.toFixed(1)}%`,
      trend: overview?.trend.ctr ?? 0,
      invert: false,
    },
    {
      title: 'Unique Visitors',
      value: overview?.uniqueSessions ?? 0,
      icon: Users,
      format: (v: number) => v.toLocaleString(),
    },
    {
      title: 'Avg Latency',
      value: overview?.avgLatencyMs ?? 0,
      icon: Zap,
      format: (v: number) => `${v}ms`,
    },
  ]

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
      {cards.map((card) => {
        const Icon = card.icon
        return (
          <Card key={card.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{card.title}</CardTitle>
              <Icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-24" />
              ) : (
                <>
                  <div className="text-2xl font-bold">{card.format(card.value)}</div>
                  <div className="mt-1 flex items-center justify-between">
                    {card.sub ? (
                      <span className="text-xs text-muted-foreground">{card.sub}</span>
                    ) : (
                      <span />
                    )}
                    {typeof card.trend === 'number' && (
                      <Trend value={card.trend} invert={card.invert} />
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}
